import { finalize } from 'rxjs/operators';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, Validators, AbstractControl, FormGroup } from '@angular/forms';
import { map as lmap, find, last, remove, isEmpty, isDate, toLower } from 'lodash';
import * as moment from 'moment-timezone';

import { SLEntity, SLEntityVersion } from './entity';
import { SLEntityService } from './entity.service';
import { NotifierService, TOption, DialogService } from '../../ui';
import { SLMovementLineOperationPipe } from './movement-line-operation.pipe';
import { SLMovementLineOwnershipPipe } from './movement-line-ownership.pipe';
import { SLMovement, SLMovementLineOperation, SLMovementLineOwnership, SLMovementLine, SLMovementAttachment, SLMovementLineShareType, SLMovementLineNature, SLMovementLineNatureByOperation, slMovementLineShareTypes } from './movement';
import { lookupParam } from '../../nav/utils';
import JSBI from 'jsbi';
import { SLEntityNewDialogComponent } from './entity-new-dialog.component';

import { FileUploader, FileItem } from 'ng2-file-upload';
import { SLAttachmentService, SLTemporayAttachment } from './attachment.service';
import { SLMovementLineShareTypePipe } from './movement-line-share-type.pipe';
import { SLMovementLineNaturePipe } from './movement-line-nature.pipe';
import { DossierModel } from '../../dossier-model/dossier-model';
import { SLMovementDismembermentDialogComponent, SLMovementDismembermentParams } from './movement-dismemberment-dialog.component';
import { SessionService } from '../../session';
import { User } from '../../user';
import { Subscription } from 'rxjs';

type ViewMode = 'view' | 'new' | 'preview';
type SubViewMode = 'default' | 'deleted' | 'draft';

interface MovementLine {
  model: SLMovementLine
  amount: FormControl
  entity: FormControl
  isExpanded: boolean
  group: FormGroup
}

type AttachmentStatus = 'START' | 'UPLOADING' | 'DIGEST' | 'DONE' | 'ERROR';

interface Attachment {
  tmp: SLTemporayAttachment
  model: SLMovementAttachment
  status: AttachmentStatus
  progress: number
}

type DraftMovementLineIssueType = 'NONE' | 'GOOD' | 'WARNING' | 'ERROR';

interface DraftMovementLineIssue {
  type: DraftMovementLineIssueType
  message: string
}

const ZERO = JSBI.BigInt(0);
const AMOUNT_REGEX = /^\d+$/;

export function validateAmount(control: AbstractControl) {
  const value = control.value;

  if (!value) {
    return {required: 'Field is required'};
  }

  if (!AMOUNT_REGEX.test(value)) {
    return {positive: 'Field should be positive number'};
  }

  if (!JSBI.GE(JSBI.BigInt(value), ZERO)) {
    return {positive: 'Field should be positive number'};
  }

  return null;
}

@Component({
  templateUrl: './movement-edit.component.html',
})
export class SLMovementEditComponent implements OnInit, OnDestroy {

  entity: SLEntity
  movement: SLMovement

  linesIssues: DraftMovementLineIssue[]

  dossierModel: DossierModel

  previewEntity: SLEntityVersion

  mode: ViewMode
  subMode: SubViewMode = 'default'
  title: string
  cancelLink: string

  lines: MovementLine[] = []
  attachments: Attachment[] = []

  previewShowHidden: boolean = true;

  lastMovement: SLMovement;

  currentUser: User;
  currentUserSubscription: Subscription;


  movementLineOperationOptions: TOption[] = lmap(['CREDIT', 'DEBIT'], (value: SLMovementLineOperation) => ({
    value,
    text: new SLMovementLineOperationPipe().transform(value),
  }));

  movementLineShareTypeOptions: TOption[] = lmap(slMovementLineShareTypes(), (value: SLMovementLineShareType) => ({
    value,
    text: new SLMovementLineShareTypePipe().transform(value),
    textShort: new SLMovementLineShareTypePipe().transform(value, 'short'),
  }));

  movementLineOwnershipOptions: TOption[] = lmap(['PP', 'UF', 'NP'], (value: SLMovementLineOwnership) => ({
    value,
    text: new SLMovementLineOwnershipPipe().transform(value),
  }));

  movementLineNatureOptions(line: SLMovementLine) {
    const natures = SLMovementLineNatureByOperation[line.operation];

    return lmap(natures, (value: SLMovementLineNature) => ({
      value,
      text: new SLMovementLineNaturePipe().transform(value),
    }));
  }

  writingDate: FormControl
  description: FormControl

  isFileOver: boolean = false
  uploader: FileUploader

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private entityService: SLEntityService,
    private attachmentService: SLAttachmentService,
    private notifier: NotifierService,
    private dialogService: DialogService,
    private sessionService: SessionService,
  ) { }

  initForm() {
    // writingDate
    this.writingDate = new FormControl(this.movement.writingDate, [
      Validators.required
    ]);

    this.writingDate.valueChanges.subscribe((value) => {
      if(isDate(value)) {
        this.movement.writingDate = moment(value).format('YYYY-MM-DD');
      }
    });

    // description
    this.description = new FormControl(this.movement.description, [
      Validators.required
    ]);

    this.description.valueChanges.subscribe((value) => {
      this.movement.description = value;
    });

    // attachments
    this.uploader = new FileUploader({
      autoUpload: false,
    });

    this.uploader.onBuildItemForm = (fileItem: FileItem, form: FormData) => {
      const attachment = fileItem.formData as Attachment;

      // Build form data
      for (const { key, value } of attachment.tmp.uploadForm) {
        form.append(key, value);
      }

      // Set the url
      fileItem.url = attachment.tmp.uploadURL;
      fileItem.withCredentials = false;

      return fileItem;
    }

    this.uploader.onProgressItem = (fileItem: FileItem, progress: number) => {
      const attachment = fileItem.formData as Attachment;
      
      attachment.progress = progress;
    }
    
    this.uploader.onCompleteItem = (fileItem: FileItem, response: any, status: number) => {
      const attachment = fileItem.formData as Attachment;

      if (status < 200 || status >= 400) {
        attachment.status = 'ERROR';
        return;
      }

      // Poll for digest
      attachment.status = 'DIGEST';
      const timeout = 1000;
      const attachmentService = this.attachmentService;

      function getDigest() {
        attachmentService
        .hashedTemporaryAttachment(attachment.model.id)
        .subscribe((result) => {
          if (result) {
            attachment.model.digest = result.digest;
            attachment.model.filesize = result.filesize;
            attachment.model.createdAt = result.createdAt;
            attachment.status = 'DONE';
          }
          else {
            setTimeout(getDigest, timeout);
          }
        }, (error) => {
          attachment.status = 'ERROR';
        });
      }

      setTimeout(getDigest, timeout);
    }
  }

  filterWritingDate() {
    const entity = this.entity;

    return (date: Date) => {
      const current = new Date();
      const d = moment([date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()]);
      const max = moment([current.getUTCFullYear(), current.getUTCMonth(), current.getUTCDate()]);

      // Max date check
      if (max.diff(d, 'days') <= 0) {
        return false;
      }

      // Min date check
      if (entity.movements.length > 1) { // > 1 because the current movement is added
        const lastMovement = this.entity.movements[this.entity.movements.length - 2];

        const minDate = new Date(lastMovement.writingDate);
        
        const min = moment([minDate.getUTCFullYear(), minDate.getUTCMonth(), minDate.getUTCDate()]);
        
        if (min.diff(d, 'days') > 1) {
          return false;
        }
      }

      return true;
    }
  }

  onAttachmentsAdded() {
    for (let fi of this.uploader.queue) {
      if (isEmpty(fi.formData)) {

        fi.formData = 'processing';

        this.addAttachment(fi);
      }
    }
  }

  addAttachment(fi: FileItem) {

    const attachment: Attachment = {
      tmp: null,
      model: new SLMovementAttachment({
        filename: fi.file.name,
        filetype: fi.file.type,
        filesize: fi.file.size,
      }),
      status: 'START',
      progress: 0,
    }

    function isSameFilename(fa: string, fb: string) {
      return toLower(fa) === toLower(fb);
    }

    // Delete any attachment with the same filename
    remove(this.attachments, ({model}) => {
      return isSameFilename(model.filename, attachment.model.filename);
    });
    
    remove(this.movement.attachments, ({filename}) => {
      return isSameFilename(filename, attachment.model.filename);
    });

    // Add the new attachment
    this.attachments.push(attachment);
    this.movement.attachments.push(attachment.model);
    fi.formData = attachment;

    this.attachmentService.createTemporaryAttachment({
      filename: fi.file.name,
      filetype: fi.file.type,
    })
    .subscribe((ta) => {
      attachment.model.id = ta.id;
      attachment.status = 'UPLOADING';
      attachment.tmp = ta;

      this.uploader.uploadItem(fi);
    });
  }

  removeAttachment(attachment: Attachment) {
    remove(this.movement.attachments, a => a === attachment.model);
    remove(this.attachments, a => a === attachment);
  }

  addDismembermentLines(event: Event = null) {

    this.dialogService.show({
      component: SLMovementDismembermentDialogComponent,
      props: {
        hideId: this.entity.id,

        onClose: () => {
          this.dialogService.hide();
        },

        onApply: ({ sourceEntity, destEntity, amount, shareType }: SLMovementDismembermentParams) => {
          this.dialogService.hide();

          const items = [
            {
              amount,
              shareType,
              entity: sourceEntity,
              operation: 'DEBIT',
              ownership: 'PP',
              nature: 'TRANSFER',
              isHidden: true,
            },
            {
              amount,
              shareType,
              entity: sourceEntity,
              operation: 'CREDIT',
              ownership: 'NP',
              nature: 'TRANSFER',
              isHidden: true,
            },
            {
              amount,
              shareType,
              entity: sourceEntity,
              operation: 'CREDIT',
              ownership: 'UF',
              nature: 'TRANSFER',
              isHidden: true,
            },
            {
              amount,
              shareType,
              entity: sourceEntity,
              operation: 'DEBIT',
              ownership: 'UF',
              nature: 'TRANSFER',
              isHidden: false,
            },
            {
              amount,
              shareType,
              entity: destEntity,
              operation: 'CREDIT',
              ownership: 'UF',
              nature: 'TRANSFER',
              isHidden: false,
            },
          ];
      
          for (let m of items) {
            this.addMovementLine(new SLMovementLine(m));
          }
        }
      }
    });

    if (event) {
      event.preventDefault();
    }

    return false;
  }

  addOneLine() {
    this.addMovementLine(new SLMovementLine({
      operation: 'CREDIT',
      shareType: 'ORD',
      ownership: 'PP',
      nature: 'TRANSFER',
      isHidden: false,
    }));

    return false;
  }

  addMovementLine(model: SLMovementLine) {

    //  shareType
    const shareType = new FormControl(model.shareType);
    shareType.valueChanges.subscribe((value) => { model.shareType = value });

    //  Ownership
    const ownership = new FormControl(model.ownership);
    ownership.valueChanges.subscribe((value) => { model.ownership = value });

    //  Entity
    const entity = new FormControl(model.entity);
    entity.valueChanges.subscribe((value) => { model.entity = value });

    //  Nature
    const nature = new FormControl(model.nature);
    nature.valueChanges.subscribe((value) => { model.nature = value });

    // Amount
    const amount = new FormControl(model.amount ? model.amount.toString(10) : null, [validateAmount]);

    amount.valueChanges.subscribe((value) => {
      if (amount.valid) {
        model.amount = JSBI.BigInt(value);
      }
      else {
        model.amount = JSBI.BigInt(0);
      }
    });

    // Operation
    const operation = new FormControl(model.operation);

    operation.valueChanges.subscribe((value) => {
      if (!value) {
        return;
      }

      model.operation = value;

      // Verify that the nature has a valid value
      const natures = SLMovementLineNatureByOperation[model.operation];

      if (natures.indexOf(model.nature) < 0) {
        model.nature = natures[0];
      }
    });

    this.movement.lines.push(model);

    this.lines.push({
      model,
      amount,
      entity,
      isExpanded: false,
      group: new FormGroup({
        operation,
        ownership,
        shareType,
        nature,
      })
    })
  }

  removeMovementLine(line: MovementLine) {
    remove(this.movement.lines, l => l === line.model);
    remove(this.lines, l => l === line);
  }

  expandMovementLine(line: MovementLine) {
    line.isExpanded = !line.isExpanded;
  }

  ngOnInit() {

    this.mode = this.route.routeConfig.data.mode;
    this.subMode = this.route.routeConfig.data.subMode || 'default';
    this.title = this.route.routeConfig.data.title;
    this.cancelLink = this.route.routeConfig.data.cancelLink;

    this.currentUserSubscription = this.sessionService.currentUser.subscribe((user) => {
      this.currentUser = user;
    });

    // Get the entity
    this.notifier.startProgress();

    this.route.data.subscribe((data) => {
      this.entity = data.entity;

      switch (this.mode) {
        case 'new': 
          {
            this.movement = new SLMovement({
              lines: [],
              attachments: [],
            });

            this.entity.movements.push(this.movement);

            this.dossierModel = data.dossierModel;
  
            this.initForm();
          }
          break;
  
        case 'view':
          {
            const movementId = lookupParam(this.route.snapshot, 'movement_id');

            if (this.subMode === 'default') {
              this.movement = find(this.entity.movements, m => m.id === movementId);
              this.title = `Mouvement N°${ this.entity.movements.indexOf(this.movement) + 1 }`;
  
              this.lastMovement = last(this.entity.movements);
            }
            else if (this.subMode === 'deleted') {
              this.movement = find(this.entity.deletedMovements, m => m.id === movementId);
              this.title = `Mouvement supprimé`;
            }
            else if (this.subMode === 'draft') {
              this.movement = find(this.entity.draftMovements, m => m.id === movementId);
              this.title = `Mouvement en attente de validation`;

              this.linesIssues = this.movement.lines.map(l => {
                const issue: DraftMovementLineIssue = {
                  type: 'NONE',
                  message: `Dernière version de l'entité`
                };

                this.entityService
                  .entityLatestState(l.entity.id)
                  .subscribe((entityState) => {
                    if (entityState.deleted) {
                      issue.type = 'ERROR';
                      issue.message = `Cette entité n'existe plus, identifiant: ${l.entity.id}`;
                    }
                    else if (entityState.version > l.entity.version) {
                      issue.type = 'WARNING';
                      issue.message = `Une version plus récente existe, version: ${entityState.version}`;
                    }
                    else {
                      issue.type = 'GOOD';
                    }
                  });

                return issue;
              });
            }
          }
          break;
      }

      this.notifier.endProgress();
    });
  }

  ngOnDestroy() {
    if (this.currentUserSubscription) {
      this.currentUserSubscription.unsubscribe();
    }
  }

  save() {
    if (!this.movement || !this.movement.valid || !this.entity.valid) {
      return;
    }

    let notifier = this.notifier;

    notifier.updateProgress(20);

    function onComplete() {
      notifier.endProgress();
    }

    switch (this.mode) {
      // Step 1: Draft
      case 'new':
        {
          this.entityService
            .previewMovement(this.entity, this.movement).pipe(
            finalize(onComplete))
            .subscribe(({entity}) => {
              this.previewEntity = entity;

              this.mode = 'preview';
            });
        }
        break;

      // Step 2: Finalization
      case 'view':
        {
          if (this.subMode === 'draft') {
            this.entityService
              .previewMovement(this.entity, this.movement, this.movement.id).pipe(
              finalize(onComplete))
              .subscribe(({entity}) => {
                this.previewEntity = entity;
  
                this.mode = 'preview';
              });
          }
        }
        break;

      case 'preview':
        {
          // Create a draft movement
          if (this.subMode === 'default') {
            this.entityService
              .createDraftMovement(this.entity, this.movement).pipe(
              finalize(onComplete))
              .subscribe((draftMovement) => {
                this.notifier.success('Movement en attente de validation créé avec succès');

                // Got to view screen
                this.router.navigate(['shares-ledger', 'entities', this.entity.id, 'draft-movements', draftMovement.id]);
              });
          }

          // Create a final movement
          else if (this.subMode === 'draft') {
            this.entityService
              .createMovement(this.entity, this.movement, this.movement.id).pipe(
              finalize(onComplete))
              .subscribe((entity) => {
                this.entity = entity;
                this.movement = last(entity.movements);

                this.notifier.success('Movement créé avec succès');

                // Got to view screen
                this.router.navigate(['shares-ledger', 'entities', entity.id, 'movements', this.movement.id]);
              });
          }
        }
        break;
    }
  }


  addEntity(event: Event, line: MovementLine) {
    this.dialogService.show({
      component: SLEntityNewDialogComponent,
      props: {

        dossierModel: this.dossierModel,

        onClose: () => {
          this.dialogService.hide();
        },

        onSave: (entity: SLEntity) => {
          this.dialogService.hide();

          line.entity.setValue(entity);
        }
      }
    });

    event.preventDefault();
  }

  cancel(event: Event) {
    if (this.mode === 'new') {
      this.router.navigate(['shares-ledger', 'entities', this.entity.id]);
    }
    else if (this.mode === 'preview') {
      this.previewEntity = null;

      if (this.subMode === 'default') {
        this.mode = 'new';
      }
      else if (this.subMode === 'draft') {
        this.mode = 'view';
      }
    }

    event.preventDefault();
    return false;
  }

  deleteMovement(event: Event) {
    this.dialogService
      .confirmDelete('Voulez-vous supprimer ce movement ?')
      .subscribe((result) => {
        if (!result) {
          return;
        }

        this.notifier.startProgress();

        if (this.subMode === 'draft') {
          this.entityService
          .deleteDraftMovement(this.movement.id).pipe(
          finalize(() => {
            this.notifier.endProgress();
          }))
          .subscribe((id) => {
            this.notifier.success('Movement supprimé avec succès');

            this.router.navigate(['shares-ledger', 'entities', this.entity.id]);
          });
        }
        else {
          this.entityService
            .deleteMovement(this.entity.id, this.movement.id).pipe(
            finalize(() => {
              this.notifier.endProgress();
            }))
            .subscribe((id) => {
              this.notifier.success('Movement supprimé avec succès');
  
              this.router.navigate(['shares-ledger', 'entities', this.entity.id]);
            });
        }

      });

    event.preventDefault();
    return false;
  }

  get canCreateMovement() {
    if (this.currentUser && this.currentUser.is_admin) {
      return true;
    }
    
    return false;
  }

}
